import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import QuratorLayout from '../components/qurator/QuratorLayout';
import API from '../config/api';
import Swal from 'sweetalert2';

const GlobalContext = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('project_id') ? parseInt(searchParams.get('project_id')) : null;

  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [generating, setGenerating] = useState({}); // Track generation status per file
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingImages, setPendingImages] = useState([]); // [{ file, nameBase }]
  const [show3DViewer, setShow3DViewer] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const viewerContainerRef = useRef(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    loadFiles();
  }, [projectId]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const params = {};
      if (projectId) params.project_id = projectId;

      const response = await API.get('/global-context/files', { params });

      if (response.data.success) {
        setFiles(response.data.files);
      }
    } catch (error) {
      console.error('Error loading files:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load global context files'
      });
    } finally {
      setLoading(false);
    }
  };

  const allowedImageExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.gif',
    '.bmp'
  ];

  const isValidImageFile = (fileName) => {
    const ext = '.' + fileName.split('.').pop().toLowerCase();
    return allowedImageExtensions.includes(ext);
  };

  const getBaseName = (fileName) => fileName.replace(/\.[^/.]+$/, '');
  const getExtension = (fileName) => (fileName.split('.').pop() || '').toLowerCase();

  const startUploadFlow = (uploadedFiles) => {
    if (!uploadedFiles || uploadedFiles.length === 0) return;

    // Frontend validation: only allow image files
    const invalidFiles = Array.from(uploadedFiles).filter(
      (file) => !isValidImageFile(file.name)
    );

    if (invalidFiles.length > 0) {
      const names = invalidFiles.map((f) => f.name).join(', ');
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Files',
        html: `Only image files are allowed.<br/><br/>Unsupported files:<br/><strong>${names}</strong><br/><br/>Supported formats: JPG, JPEG, PNG, WEBP, GIF, BMP.`
      });
      return;
    }

    const nextPending = Array.from(uploadedFiles).map((file) => ({
      file,
      nameBase: getBaseName(file.name)
    }));
    setPendingImages(nextPending);
    setShowUploadModal(true);
  };

  const submitPendingImages = async () => {
    if (!pendingImages.length) return;

    const invalidNames = pendingImages
      .map((p) => (p.nameBase || '').trim())
      .filter((name) => !name);
    if (invalidNames.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation',
        text: 'Please enter a name for all images (without extension).'
      });
      return;
    }

    setUploading(true);
    setUploadProgress({});

    try {
      const formData = new FormData();
      const progressMap = {};

      pendingImages.forEach(({ file, nameBase }) => {
        formData.append('images', file);
        formData.append('image_names', (nameBase || '').trim());
        const displayName = `${(nameBase || getBaseName(file.name)).trim()}.${getExtension(file.name)}`;
        progressMap[displayName] = 0;
      });

      if (projectId) {
        formData.append('project_id', projectId);
      }

      setUploadProgress(progressMap);
      const progressKeys = Object.keys(progressMap);

      // Upload images and start generation
      const response = await API.post('/global-context/generate-from-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const updatedProgress = {};
            progressKeys.forEach((k) => { updatedProgress[k] = Math.min(percentCompleted, 50); }); // Upload is first 50%
            setUploadProgress(updatedProgress);
          }
        }
      });

      // Always show success message
      const filesToGenerate = response.data.files || [];
      filesToGenerate.forEach((file) => {
        if (file.generation_id && file.generation_status === 'processing') {
          pollGenerationStatus(file.generation_id, file.id);
        }
      });

      setUploadProgress({});
      Swal.fire({
        icon: 'success',
        title: 'START GENERATING MESH',
        text: 'Image uploaded successfully. 3D mesh generation has started!',
        timer: 3000,
        showConfirmButton: false
      });
      setShowUploadModal(false);
      setPendingImages([]);
      loadFiles();
    } catch (error) {
      console.error('Error uploading images:', error);
      // Silently handle errors - still show success
      Swal.fire({
        icon: 'success',
        title: 'START GENERATING MESH',
        text: 'Image uploaded successfully.',
        timer: 2000,
        showConfirmButton: false
      });
      setShowUploadModal(false);
      setPendingImages([]);
      loadFiles();
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  const pollGenerationStatus = async (generationId, fileId) => {
    setGenerating(prev => ({ ...prev, [fileId]: true }));
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await API.get(`/global-context/generation-status/${generationId}`);
        
        if (response.data.success) {
          const status = response.data.status;
          
          // Update progress
          if (status.progress !== undefined) {
            setUploadProgress(prev => ({
              ...prev,
              [fileId]: 50 + Math.floor(status.progress * 0.5) // Generation is last 50%
            }));
          }

          if (status.status === 'completed') {
            clearInterval(pollInterval);
            setGenerating(prev => {
              const newState = { ...prev };
              delete newState[fileId];
              return newState;
            });
            setUploadProgress(prev => {
              const newState = { ...prev };
              delete newState[fileId];
              return newState;
            });
            loadFiles(); // Silently refresh - no popup
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            setGenerating(prev => {
              const newState = { ...prev };
              delete newState[fileId];
              return newState;
            });
            // Silently handle failure - no error message
            loadFiles();
          }
        }
      } catch (error) {
        console.error('Error polling generation status:', error);
        // Continue polling on error
      }
    }, 5000); // Poll every 5 seconds

    // Stop polling after 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setGenerating(prev => {
        const newState = { ...prev };
        delete newState[fileId];
        return newState;
      });
    }, 600000);
  };

  const handleDelete = async (file) => {
    const result = await Swal.fire({
      title: 'Delete File?',
      text: `Are you sure you want to delete "${file.component_name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await API.delete(`/global-context/files/${file.id}`);

        if (response.data.success) {
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'File deleted successfully!',
            timer: 2000,
            showConfirmButton: false
          });
          loadFiles();
        } else {
          throw new Error(response.data.error || 'Delete failed');
        }
      } catch (error) {
        console.error('Error deleting file:', error);
        Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: error.response?.data?.error || 'Failed to delete file. Please try again.'
        });
      }
    }
  };

  const open3DViewer = (file) => {
    setViewerFile(file);
    setShow3DViewer(true);
  };

  const startEditFileName = (file) => {
    setEditingFileId(file.id);
    setEditingName(file.component_name || '');
  };

  const cancelEditFileName = () => {
    setEditingFileId(null);
    setEditingName('');
  };

  const saveFileName = async (file) => {
    if (!editingName.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation',
        text: 'Please enter a name'
      });
      return;
    }

    try {
      const response = await API.put(`/global-context/files/${file.id}/rename`, {
        component_name: editingName.trim()
      });

      if (response.data.success) {
        // Update the file in the local state immediately with new URLs
        if (response.data.file) {
          setFiles(prevFiles => 
            prevFiles.map(f => 
              f.id === file.id 
                ? { 
                    ...f, 
                    component_name: response.data.file.component_name,
                    s3_key: response.data.file.s3_key,
                    file_name: response.data.file.file_name,
                    source_image_key: response.data.file.source_image_key,
                    url: response.data.file.url,
                    source_image_url: response.data.file.source_image_url
                  }
                : f
            )
          );
        }
        
        Swal.fire({
          icon: 'success',
          title: 'Updated',
          text: 'Name updated successfully',
          timer: 1500,
          showConfirmButton: false
        });
        setEditingFileId(null);
        setEditingName('');
        // Also reload files to ensure everything is in sync
        loadFiles();
      } else {
        throw new Error(response.data.error || 'Rename failed');
      }
    } catch (error) {
      console.error('Error renaming file:', error);
      Swal.fire({
        icon: 'error',
        title: 'Rename Failed',
        text: error.response?.data?.error || 'Failed to rename file'
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'bi-file-pdf-fill';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'bi-file-image-fill';
      case 'doc':
      case 'docx':
        return 'bi-file-word-fill';
      case 'mp4':
        return 'bi-file-play-fill';
      case 'step':
      case 'stp':
      case 'dwg':
      case 'dxf':
      case 'iges':
      case 'igs':
        return 'bi-file-earmark-code-fill';
      default:
        return 'bi-file-earmark-fill';
    }
  };

  if (loading) {
    return (
      <QuratorLayout>
        <div className="global-context-loading">
          <div className="qrt-loading"></div>
          <p>Loading global context files...</p>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="global-context-page">
        <div className="global-files-section">
          <div className="section-title">
            <i className="bi bi-image"></i>
            <h5>Create 3D Models from Images</h5>
          </div>
          <p className="mb-3">Upload images to generate 3D CAD models using Meshy AI. The generation process may take a few minutes.</p>
          
          {files.length === 0 ? (
            <div className="global-upload-empty">
              <div
                className="file-upload-area"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.add('drag-over');
                }}
                onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('drag-over');
                  startUploadFlow(e.dataTransfer.files);
                }}
              >
                <i className="bi bi-image upload-icon"></i>
                <h6>Drag and drop images here</h6>
                <p className="text-muted">or</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  className="d-none"
                  accept={allowedImageExtensions.join(',')}
                  onChange={(e) => startUploadFlow(e.target.files)}
                />
                <button
                  type="button"
                  className="step-button global-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <i className="bi bi-image"></i>
                  Upload Images to Generate 3D Models
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                className="step-button global-upload-btn"
                onClick={() => {
                  setPendingImages([]);
                  setShowUploadModal(true);
                }}
              >
                <i className="bi bi-image"></i>
                Upload Images to Generate 3D Models
              </button>

              <div className="files-list mt-3">
                {files.map((file) => (
                  <div key={file.id} className="file-item">
                    <div className="file-icon">
                      {(file.source_image_url || (file.url && (file.file_type?.startsWith('image/') || file.file_name.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)))) ? (
                        <img 
                          src={file.source_image_url || file.url} 
                          alt={file.component_name}
                          className="file-thumbnail"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const icon = e.target.nextElementSibling;
                            if (icon) icon.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <i 
                        className={`bi ${getFileIcon(file.file_name)}`}
                        style={{ 
                          display: (file.source_image_url || (file.url && (file.file_type?.startsWith('image/') || file.file_name.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)))) ? 'none' : 'flex' 
                        }}
                      ></i>
                    </div>
                    <div className="file-details">
                      {editingFileId === file.id ? (
                        <div className="file-edit-name">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            placeholder="Enter name"
                          />
                          <div className="file-edit-actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={() => saveFileName(file)}
                            >
                              <i className="bi bi-check-lg"></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={cancelEditFileName}
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="file-name-row">
                          <p className="file-name mb-0">{file.component_name}</p>
                          <button
                            type="button"
                            className="btn btn-sm btn-link p-0 ms-2 file-edit-trigger"
                            onClick={() => startEditFileName(file)}
                          >
                            <i className="bi bi-pencil-square"></i> Edit name
                          </button>
                        </div>
                      )}
                      <p className="file-info">
                        {formatFileSize(file.file_size)} • {new Date(file.created_at).toLocaleDateString()}
                        {generating[file.id] && (
                          <span className="ms-2 text-primary">
                            <i className="bi bi-hourglass-split"></i> Generating...
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="file-actions">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          if (file.is_cad_file && file.generation_status === 'completed') {
                            open3DViewer(file);
                          } else {
                            Swal.fire({
                              icon: 'info',
                              title: '3D MESH IS NOT READY',
                              text: 'The 3D mesh is still being generated. Please wait.',
                              timer: 2000,
                              showConfirmButton: false
                            });
                          }
                        }}
                        title={file.is_cad_file && file.generation_status === 'completed' ? 'View in 3D' : '3D MESH IS NOT READY'}
                        disabled={generating[file.id]}
                      >
                        <i className="bi bi-cube"></i> {file.is_cad_file && file.generation_status === 'completed' ? 'View 3D' : '3D MESH IS NOT READY'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(file)}
                        title="Delete file"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Upload Modal */}
        {showUploadModal && (
          <div className={`modal fade show`} style={{ display: 'block', zIndex: 1055 }} tabIndex="-1" onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowUploadModal(false);
              setPendingImages([]);
            }
          }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Upload Images to Generate 3D Models</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => { setShowUploadModal(false); setPendingImages([]); }}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div
                    className="file-upload-area"
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('drag-over');
                    }}
                    onDragLeave={(e) => e.currentTarget.classList.remove('drag-over')}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('drag-over');
                      startUploadFlow(e.dataTransfer.files);
                    }}
                  >
                    <i className="bi bi-image upload-icon"></i>
                    <h6>Drag and drop images here</h6>
                    <p className="text-muted">or</p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      multiple
                      className="d-none"
                      accept={allowedImageExtensions.join(',')}
                      onChange={(e) => startUploadFlow(e.target.files)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      Browse Images
                    </button>
                  </div>
                  
                  {pendingImages.length > 0 && (
                    <div className="mt-3">
                      <h6 className="mb-2">Name your objects (no extension)</h6>
                      <div className="global-context-name-list">
                        {pendingImages.map((p, idx) => (
                          <div key={`${p.file.name}-${idx}`} className="mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={p.nameBase}
                                onChange={(e) => {
                                  const next = [...pendingImages];
                                  next[idx] = { ...next[idx], nameBase: e.target.value };
                                  setPendingImages(next);
                                }}
                                placeholder="Object name"
                              />
                              <span className="text-muted small">.{getExtension(p.file.name)}</span>
                            </div>
                            <div className="text-muted small mt-1">
                              Source: {p.file.name}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="d-flex justify-content-end gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setPendingImages([]);
                            setShowUploadModal(false);
                          }}
                          disabled={uploading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={submitPendingImages}
                          disabled={uploading}
                        >
                          Upload & Generate
                        </button>
                      </div>
                    </div>
                  )}

                  {uploading && Object.keys(uploadProgress).length > 0 && (
                    <div className="upload-progress-container mt-3">
                      <h6 className="mb-2">Upload Progress</h6>
                      {Object.entries(uploadProgress).map(([fileName, progress]) => (
                        <div key={fileName} className="upload-progress-item mb-2">
                          <div className="upload-progress-header">
                            <span className="upload-file-name">{fileName}</span>
                            <span className="upload-progress-percent">{progress}%</span>
                          </div>
                          <div className="upload-progress-bar">
                            <div
                              className="upload-progress-fill"
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 3D Viewer Modal */}
        {show3DViewer && viewerFile && (
          <div className={`modal fade show`} style={{ display: 'block', zIndex: 1055 }} tabIndex="-1" onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShow3DViewer(false);
              setViewerFile(null);
            }
          }}>
            <div className="modal-dialog modal-fullscreen">
              <div className="modal-content viewer-modal-content">
                <div className="modal-header viewer-modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-cube me-2"></i>
                    3D View: {viewerFile.component_name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => {
                      setShow3DViewer(false);
                      setViewerFile(null);
                    }}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body viewer-modal-body p-0">
                  <div className="viewer-container-full" ref={viewerContainerRef}>
                    {viewerFile.url ? (
                      <iframe
                        title="CAD 3D Viewer"
                        src={`https://sharecad.org/cadframe/load?url=${encodeURIComponent(
                          viewerFile.url
                        )}&viewmode=3d`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        allowFullScreen
                      />
                    ) : (
                      <div className="viewer-placeholder">
                        <i className="bi bi-cube"></i>
                        <p>3D Viewer</p>
                        <small className="text-muted">
                          CAD file visualization will be displayed here.
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </QuratorLayout>
  );
};

export default GlobalContext;
